import { isEmpty, isNil } from 'lodash';
import { CSSProperties, PointerEvent as ReactPointerEvent, useCallback } from 'react';
import { Button } from '@shared/components/Button';
import { Modal } from '@shared/components/Modal';
import { Text } from '@shared/components/Text';
import { ASK_SESSION_HISTORY, useFeatureFlag } from '@shared/util/featureFlags';
import { useCurrentBusiness } from '@shared/util/hooks/useCurrentBusiness';
import { HotKey, useHotkeyBinding } from '@shared/util/hotkeys';
import { i18n } from '@shared/util/i18n';
import { useAsk } from '../useAsk';
import { ResizeDirection, usePanelLayout } from '../usePanelLayout';
import { askScopeHref } from '../util/askScopeHref';
import { handleTranscriptLinkClick } from '../util/handleTranscriptLinkClick';
import { AskConversation } from './AskConversation';
import { AskSessionHistoryMenu } from './AskSessionHistoryMenu';
import styles from './AskPanel.module.css';

const RESIZE_DIRECTIONS: ResizeDirection[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

/**
 * Pointer targets in the header that must never start a panel drag: its own controls, and
 * anything inside a menu hung off one of them. A menu is a surface in its own right — it just
 * happens to render inside the bar's DOM, so its pointer events bubble here — and dragging the
 * panel out from under a field someone is trying to click into is never what they meant.
 * `[role='menu']` is the menu root, so `closest` catches every row, button and input within it.
 */
const NON_DRAG_TARGETS = ['button', 'a', 'input', 'textarea', 'select', '[role="menu"]'].join(', ');

export function AskPanel() {
  const { isOpen, scope, isStreaming, pendingChoice, close, newSession } = useAsk();
  const isSessionHistoryEnabled = useFeatureFlag(ASK_SESSION_HISTORY);
  const business = useCurrentBusiness();
  const { layout, isExpanded, isAnimating, animationMs, startDrag, startResize, toggleExpand } =
    usePanelLayout();
  const askGlobalHotkey = useHotkeyBinding('askGlobal');
  const askWorkspaceHotkey = useHotkeyBinding('askWorkspace');
  const askCardHotkey = useHotkeyBinding('askCard');

  const contextLabel = scope?.type === 'org' ? business?.name : scope?.label;
  // The heading is the fastest way to check what the conversation is about, so it links to the
  // card or workspace itself wherever there is one to link (see `askScopeHref`).
  const contextHref = isNil(scope) ? undefined : askScopeHref(scope);

  const handleNewSession = useCallback(() => void newSession(), [newSession]);

  const onHeaderPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if ((event.target as HTMLElement).closest(NON_DRAG_TARGETS)) {
        return;
      }
      startDrag(event);
    },
    [startDrag],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close();
      }
    },
    [close],
  );

  const layoutStyle: CSSProperties = {
    position: 'fixed',
    left: layout.left,
    top: layout.top,
    width: layout.width,
    height: layout.height,
    right: 'auto',
    bottom: 'auto',
    margin: 0,
    minWidth: 0,
    minHeight: 0,
    maxWidth: 'none',
    maxHeight: 'none',
    transition: isAnimating
      ? `left ${animationMs}ms ease, top ${animationMs}ms ease, width ${animationMs}ms ease, height ${animationMs}ms ease`
      : undefined,
  };

  return (
    <Modal
      mode="floating"
      animate
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      className={styles.panel}
      style={layoutStyle}
      aria-label={i18n('ask.title')}
    >
      {/* Any Ask hotkey closes the panel so the same chord toggles it shut. */}
      <HotKey hotkey={askGlobalHotkey} callback={close} />
      <HotKey hotkey={askWorkspaceHotkey} callback={close} />
      <HotKey hotkey={askCardHotkey} callback={close} />
      <div className={styles.dragHandle} onPointerDown={onHeaderPointerDown}>
        <Modal.TitleBar
          title={
            <div className={styles.titleBlock}>
              <Text size="h4" weight="semibold" numberOfLines={1}>
                {i18n('ask.title')}
              </Text>
              {!isEmpty(contextLabel) &&
                (isNil(contextHref) ? (
                  <span className={styles.contextLabel}>{contextLabel}</span>
                ) : (
                  <a
                    href={contextHref}
                    className={[styles.contextLabel, styles.contextLink].join(' ')}
                    onClick={handleTranscriptLinkClick}
                    data-testid="ask-panel-context-link"
                  >
                    {contextLabel}
                  </a>
                ))}
            </div>
          }
          startContent={
            <>
              <Modal.CloseButton onClick={close} testID="ask-panel-close" />
              {/* Left out rather than disabled while the flag is off: with no history there is
                  nothing for the button to do, and the close button beside it holds the corner
                  either way, so nothing shifts. */}
              {isSessionHistoryEnabled && <AskSessionHistoryMenu />}
            </>
          }
          endContent={
            <>
              <Button
                icon="plus"
                iconOnly
                iconGridSize={3.75}
                variant="ghost"
                // Disabled rather than hidden: a control that comes and goes in the title
                // bar makes the resize button jump. Starting over mid-answer is almost
                // always a misclick, and a pending replace is already a question about
                // clearing this conversation; answer that one. An empty session stays
                // enabled — resetting it is a no-op, and disabling it there would leave the
                // button dead to hover under the drag handle for most of the panel's life.
                disabled={isStreaming || !isNil(pendingChoice)}
                onClick={handleNewSession}
                label={i18n('ask.newSession')}
                testID="ask-panel-new-session"
                tooltip={i18n('ask.newSession')}
                tooltipPosition="bottom"
              />
              <Modal.ResizeButton
                isExpanded={isExpanded}
                onClick={toggleExpand}
                testID="ask-panel-resize"
              />
            </>
          }
        />
      </div>

      <AskConversation />

      {RESIZE_DIRECTIONS.map(direction => (
        <div
          key={direction}
          className={[styles.resizeHandle, styles[`resize_${direction}`]].join(' ')}
          onPointerDown={event => startResize(event, direction)}
        />
      ))}
    </Modal>
  );
}
